import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useStorage, useDebounceFn } from "@vueuse/core"
import { useRoute, useRouter } from 'vue-router'
import { extractDate } from "@/utils/datetime"
import { saveBlob } from '@/utils/saveBlob'
import { parseFilenameFromContentDisposition } from "@/utils/contentDisposition"

// /**
//  * Extract the filename from a Content-Disposition header value.
//  * Supports both `filename*=UTF-8''...` (RFC 5987, preferred) and `filename="..."`.
//  *
//  * @param {string|null} contentDisposition - Raw header value
//  * @returns {string|null} Decoded filename or null when not present
//  */
// const parseFilenameFromContentDisposition = (contentDisposition) => {
//   if (!contentDisposition) return null
//
//   // RFC 5987 encoded form takes precedence (handles non-ASCII names)
//   const encodedMatch = contentDisposition.match(/filename\*=(?:UTF-8'')?([^;]+)/i)
//   if (encodedMatch?.[1]) {
//     try {
//       return decodeURIComponent(encodedMatch[1].trim().replace(/^["']|["']$/g, ''))
//     } catch {
//       // fall through to plain filename
//     }
//   }
//
//   const plainMatch = contentDisposition.match(/filename="?([^";]+)"?/i)
//
//   return plainMatch?.[1]?.trim() || null
// }


/**
 * Storage Products List Store
 *
 * Manages warehouse products data including:
 * - Fetching products from GET /store/index (with BE pagination)
 * - Fetching filters from GET /store/filters
 * - Server-side filtering (suppliers, producers, categories, types, tax, availability)
 * - Server-side sorting
 * - Server-side search
 */
export const useStorageProductsListStore = defineStore('storageProductsListStore', () => {

  // ========================================
  // State
  // ========================================

  /** Global loading state for products operations */
  const isLoading = ref(true)

  /** Products for the current page from API */
  const allProducts = ref([])

  /** Whether data has been fetched */
  const isLoaded = ref(false)

  const route = useRoute()
  const router = useRouter()

  /**
   * Pagination metadata from API
   * @property {number} total - Total products matching current filters
   * @property {number} perPage - Items per page
   * @property {number} page - Current page
   * @property {number} totalAll - Total products (regardless of filters)
   */
  const meta = ref({
    total: 0,
    perPage: 11,
    page: 1,
    totalAll: 0,
  })

  /**
   * Available filters from API
   * @property {Array<{id: string, name: string}>} salonerSuppliers
   * @property {Array<{id: string, name: string}>} producers
   * @property {Array<{id: string, name: string}>} categories
   * @property {Array<{id: string, name: string}>} types
   * @property {Array<{id: string, name: string}>} tax
   * @property {Array<{id: string, name: string}>} availability
   */
  const availableFilters = ref({
    salonerSuppliers: [],
    producers: [],
    categories: [],
    types: [],
    tax: [],
    availability: [],
  })

  /** Loading state for filters */
  const isLoadingFilters = ref(false)

  /** Search query for filtering products */
  const searchQuery = useStorage('storage-product-list-search', '', localStorage, {
    listenToStorageChanges: false,
  })
  /** Selected supplier filter (salonerSupplierId) */
  const selectedSupplier = useStorage('storage-product-list-selected-supplier', '', localStorage, {
    listenToStorageChanges: false,
  })

  /** Selected producer filter */
  const selectedProducer = useStorage('storage-product-list-selected-producer', '', localStorage, {
    listenToStorageChanges: false,
  })

  /** Selected category filter */
  const selectedCategory = useStorage('storage-product-list-selected-category', '', localStorage, {
    listenToStorageChanges: false,
  })

  /** Selected type filter (1=sale, 2=usage, 3=both) */
  const selectedType = useStorage('storage-product-list-selected-type', '', localStorage, {
    listenToStorageChanges: false,
  })

  /** Selected tax filter ("-1"=zw, "0", "5", "8", "23") */
  const selectedTax = useStorage('storage-product-list-selected-tax', '', localStorage, {
    listenToStorageChanges: false,
  })

  /** Selected availability filter ("out_of_stock", "ending", "available") */
  const selectedAvailability = useStorage('storage-product-list-selected-availabilityy', '', localStorage, {
    listenToStorageChanges: false,
  })

  /**
   * Sorting configuration
   * @property {string|null} column - Column to sort by (id, name, createdDate, category, producer, price, buyPrice, tax, type)
   * @property {string} direction - Sort direction (asc, desc)
   */
  // const sorting = ref({
  //   column: 'name',
  //   direction: 'asc',
  // })
  const sorting = useStorage('storage-product-list-sorting', { column: 'name', direction: 'asc' }, localStorage, {
    listenToStorageChanges: false,
  })

  // ========================================
  // Getters
  // ========================================


  /**
   * Total count of products in import list (all, regardless of filters)
   * @returns {number}
   */
  const totalProductsCount = computed(() => meta.value.totalAll)

  /**
   * Filtered products count (matching current filters)
   * @returns {number}
   */
  const filteredProductsCount = computed(() => meta.value.total)

  /**
   * Total pages based on pagination metadata
   * @returns {number}
   */
  const totalPages = computed(() => {
    if (!meta.value.total || !meta.value.perPage) return 1

    return Math.ceil(meta.value.total / meta.value.perPage)
  })

  /**
   * Current page from pagination metadata
   * @returns {number}
   */
  const currentPage = computed(() => meta.value.page)

  /**
   * Count of active filters
   * @returns {number}
   */
  const filtersCount = computed(() => {
    let count = 0
    if (selectedSupplier.value) count++
    if (selectedProducer.value) count++
    if (selectedCategory.value) count++
    if (selectedType.value) count++
    if (selectedTax.value) count++
    if (selectedAvailability.value) count++

    return count
  })

  // ========================================
  // Actions
  // ========================================

  /**
   * Clear all filters
   */
  const clearFilters = async () => {
    selectedSupplier.value = null
    selectedProducer.value = null
    selectedCategory.value = null
    selectedType.value = null
    selectedTax.value = null
    selectedAvailability.value = null
    searchQuery.value = ''

    await router.replace({
      path: route.path,
      query: null,
    })

    await fetchProducts({
      page: 1,
      perPage: meta.value.perPage,
      force: true,
    })
  }

  /**
   * Fetch available filters from the backend
   *
   * @returns {Promise<boolean>} True if successful, false otherwise
   */
  const fetchFilters = async () => {
    isLoadingFilters.value = true

    try {
      const response = await $backend('/store/filters', {
        method: 'GET',
      })

      if (response.success) {
        availableFilters.value = {
          salonerSuppliers: response.data.filters.salonerSuppliers || [],
          producers: response.data.filters.producers || [],
          categories: response.data.filters.categories || [],
          types: response.data.filters.types || [],
          tax: response.data.filters.tax || [],
          availability: response.data.filters.availability || [],
        }

        return true
      }
    } catch (error) {
      console.error('Error fetching store filters:', error)

      return false
    } finally {
      isLoadingFilters.value = false
    }

    return false
  }

  /**
   * Build query parameters shared by /store/index and /store/export
   * (sorting + active filters, without pagination)
   *
   * @returns {URLSearchParams}
   */
  const buildQueryParams = () => {
    const params = new URLSearchParams()

    // Add sorting if specified
    if (sorting.value.column) {
      params.append('orders[0][column]', sorting.value.column)
      params.append('orders[0][direction]', sorting.value.direction)
    }

    // Add filters
    if (searchQuery.value.trim()) {
      params.append('filters[search]', searchQuery.value.trim())
    }
    if (selectedSupplier.value) {
      params.append('filters[salonerSupplierId]', selectedSupplier.value)
    }
    if (selectedProducer.value) {
      params.append('filters[producer]', selectedProducer.value)
    }
    if (selectedCategory.value) {
      params.append('filters[category]', selectedCategory.value)
    }
    if (selectedType.value) {
      params.append('filters[type]', selectedType.value)
    }
    if (selectedTax.value) {
      params.append('filters[tax]', selectedTax.value)
    }
    if (selectedAvailability.value) {
      params.append('filters[availability]', selectedAvailability.value)
    }

    return params
  }

  /**
   * Fetch products from the backend (with pagination and server-side filters)
   *
   * @param {Object} options - Fetch options
   * @param {number} options.page - Page number
   * @param {number} options.perPage - Items per page
   * @param {boolean} options.force - Force refetch even if loaded
   * @returns {Promise<boolean>} True if successful, false otherwise
   */
  const fetchProducts = async ({ page = 1, perPage = 11, force = false } = {}) => {
    // Skip fetching if data is already loaded for the same page
    if (!force && isLoaded.value && meta.value.page === page) {
      return true
    }

    isLoading.value = true

    try {
      const params = buildQueryParams()

      params.append('page', page.toString())
      params.append('perPage', perPage.toString())

      const response = await $backend(`/store/index?${params.toString()}`, {
        method: 'GET',
      })

      if (response.success) {
        // If we are on far away page and there is no results for this page we go to 1st page instead
        if (response.data.products.length === 0 && page !== 1 && response.meta?.total > 0) {
          isLoaded.value = false
          const lastPage = Math.ceil(response.meta.total / (response.meta?.perPage ?? perPage))
          await router.push({ path: '/storage/product-list', query: { page: lastPage } })

          return true
        }

        allProducts.value = response.data.products || []
        meta.value = {
          total: response.meta?.total ?? 0,
          perPage: response.meta?.perPage ?? perPage,
          page: response.meta?.page ?? page,
          totalAll: response.meta?.totalAll ?? 0,
        }

        isLoaded.value = true
        isLoading.value = false

        return true
      }

      isLoading.value = false
    } catch (error) {
      isLoading.value = false
      //console.error('Error fetching store products:', error)

      return false
    }

    return false
  }

  /** Loading state for XLSX report download */
  const isDownloading = ref(false)

  /**
   * Export products report to XLSX from GET /store/export
   * Respects current filters, search and sorting (same params as /store/index)
   *
   * @returns {Promise<boolean>} True if successful, false otherwise
   */
  const exportProducts = async () => {
    if (isDownloading.value)
      return false

    isDownloading.value = true
    try {
      const params = buildQueryParams()
      const queryString = params.toString() ? `?${params.toString()}` : ''

      // Use .raw() to access the full FetchResponse (headers + blob),
      // so we can read the filename from the Content-Disposition header.
      // NOTE: backend must send `Access-Control-Expose-Headers: Content-Disposition`
      // for this header to be readable cross-origin.
      const response = await $backend.raw(
        `/store/export${queryString}`,
        {
          method: 'GET',
          responseType: 'blob',
          disableAbortController: true,
        })

      const blob = response._data

      // Fallback name when the backend doesn't provide one (or header is not exposed)
      const fallbackFilename = `magazyn_produkty_${extractDate(new Date(), '-')}.xlsx`
      const filename = parseFilenameFromContentDisposition(response.headers.get('content-disposition')) || fallbackFilename

      return await saveBlob(blob, filename)
    } catch (e) {

      return false
    } finally {
      isDownloading.value = false
    }
  }

  /**
   * Change page and refetch products
   *
   * @param {number} page - Page number
   * @returns {Promise<boolean>}
   */
  const changePage = async (page) => {
    return await fetchProducts({
      page,
      perPage: meta.value.perPage,
      force: true,
    })
  }

  /**
   * Change items per page and refetch products (reset to page 1)
   *
   * @param {number} perPage - Items per page
   * @returns {Promise<boolean>}
   */
  const changePerPage = async (perPage) => {
    return await fetchProducts({
      page: 1,
      perPage,
      force: true,
    })
  }

  /**
   * Set search query (updates state only, no fetch)
   *
   * @param {string} query - Search query
   */
  const setSearchQuery = (query) => {
    searchQuery.value = query
  }

  /**
   * Debounced action: updates search query and refetches products after 300 ms of inactivity
   *
   * @param {string} query - Search query
   * @returns {Promise<boolean>}
   */
  const debouncedSetSearchQuery = useDebounceFn(async (query) => {
    searchQuery.value = query

    return await fetchProducts({
      page: 1,
      perPage: meta.value.perPage,
      force: true,
    })
  }, 300)

  /**
   * Set supplier filter and refetch products (reset to page 1)
   *
   * @param {string|null} id - Supplier ID or null to clear
   * @returns {Promise<boolean>}
   */
  const setSupplierFilter = async (id) => {
    selectedSupplier.value = id

    return await fetchProducts({
      page: 1,
      perPage: meta.value.perPage,
      force: true,
    })
  }

  /**
   * Set producer filter and refetch products (reset to page 1)
   *
   * @param {string|null} id - Producer ID or null to clear
   * @returns {Promise<boolean>}
   */
  const setProducerFilter = async (id) => {
    selectedProducer.value = id

    return await fetchProducts({
      page: 1,
      perPage: meta.value.perPage,
      force: true,
    })
  }

  /**
   * Set category filter and refetch products (reset to page 1)
   *
   * @param {string|null} id - Category ID or null to clear
   * @returns {Promise<boolean>}
   */
  const setCategoryFilter = async (id) => {
    selectedCategory.value = id

    return await fetchProducts({
      page: 1,
      perPage: meta.value.perPage,
      force: true,
    })
  }

  /**
   * Set type filter and refetch products (reset to page 1)
   *
   * @param {string|null} id - Type ID or null to clear
   * @returns {Promise<boolean>}
   */
  const setTypeFilter = async (id) => {
    selectedType.value = id

    return await fetchProducts({
      page: 1,
      perPage: meta.value.perPage,
      force: true,
    })
  }

  /**
   * Set tax filter and refetch products (reset to page 1)
   *
   * @param {string|null} id - Tax ID or null to clear
   * @returns {Promise<boolean>}
   */
  const setTaxFilter = async (id) => {
    selectedTax.value = id

    return await fetchProducts({
      page: 1,
      perPage: meta.value.perPage,
      force: true,
    })
  }

  /**
   * Set availability filter and refetch products (reset to page 1)
   *
   * @param {string|null} id - Availability ID or null to clear
   * @returns {Promise<boolean>}
   */
  const setAvailabilityFilter = async (id) => {
    selectedAvailability.value = id

    return await fetchProducts({
      page: 1,
      perPage: meta.value.perPage,
      force: true,
    })
  }

  /**
   * Set sorting and refetch products (reset to page 1)
   *
   * @param {string|null} column - Column to sort by
   * @param {string} direction - Sort direction (asc/desc)
   * @returns {Promise<boolean>}
   */
  const setSorting = async (column, direction = 'asc') => {
    sorting.value = { column, direction }

    return await fetchProducts({
      page: 1,
      perPage: meta.value.perPage,
      force: true,
    })
  }

  /**
   * Clear all filters and refetch products
   *
   * @returns {Promise<boolean>}
   */
  const clearAllFilters = async () => {
    searchQuery.value = ''
    selectedSupplier.value = null
    selectedProducer.value = null
    selectedCategory.value = null
    selectedType.value = null
    selectedTax.value = null
    selectedAvailability.value = null
    sorting.value = { column: 'name', direction: 'asc' }

    return await fetchProducts({
      page: 1,
      perPage: meta.value.perPage,
      force: true,
    })
  }

  /** Loading/saving state for single product edit dialog */
  const isLoadingProduct = ref(false)
  const isSavingProduct = ref(false)

  /**
   * Fetch single product details by ID
   *
   * @param {number|string} id - Product ID
   * @returns {Promise<Object|null>} Product data or null on failure
   */
  const fetchProduct = async (id) => {
    isLoadingProduct.value = true
    try {
      const response = await $backend(`/store/show?id=${id}`, {
        method: 'GET',
      })

      if (response.success) {
        return response.data
      }

      return null
    } catch (error) {

      return null
    } finally {
      isLoadingProduct.value = false
    }
  }

  /**
   * Save product changes
   *
   * @param {Object} productData - Product data to save
   * @returns {Promise<Object|null>} Response or null on failure
   */
  const saveProduct = async (productData) => {
    isSavingProduct.value = true
    try {
      return await $backend('/store/save', {
        method: 'POST',
        body: productData,
      })
    } catch (error) {

      return null
    } finally {
      isSavingProduct.value = false
    }
  }

  /**
   * Delete a product by ID
   *
   * @param {number|string} id - Product ID
   * @returns {Promise<{success: boolean, message: string}>}
   */
  const deleteProduct = async (id) => {
    try {
      const response = await $backend('/store/delete', {
        method: 'POST',
        body: [
          { "storeId": id },
        ],
      })

      return {
        success: response.success || false,
        message: response.message || '',
      }
    } catch (error) {
      return {
        success: false,
        message: error.message || '',
      }
    } finally {
      //isLoading.value = false
    }
  }

  /**
   * Delete products by IDs
   *
   * @param {Array<number|string>} ids - Product IDs
   * @returns {Promise<{success: boolean, message: string}>}
   */
  const deleteProducts = async (ids) => {

    try {
      const response = await $backend('/store/delete', {
        method: 'POST',
        body: ids.map((id) => ({ storeId: id })),
      })

      return {
        success: response.success || false,
        message: response.message || '',
      }
    } catch (error) {
      return {
        success: false,
        message: error.message || '',
      }
    } finally {
      //isLoading.value = false
    }
  }

  /**
   * Reset store to initial state
   */
  const resetStore = () => {
    isLoading.value = false
    isLoadingFilters.value = false
    allProducts.value = []
    isLoaded.value = false
    meta.value = { total: 0, perPage: 25, page: 1, totalAll: 0 }
    availableFilters.value = {
      salonerSuppliers: [],
      producers: [],
      categories: [],
      types: [],
      tax: [],
      availability: [],
    }
    //searchQuery.value = ''
    //selectedSupplier.value = null
    //selectedProducer.value = null
    //selectedCategory.value = null
    //selectedType.value = null
    //selectedTax.value = null
    //selectedAvailability.value = null
    //sorting.value = { column: 'name', direction: 'asc' }
  }

  // ========================================
  // Exports
  // ========================================

  return {
    // State
    isLoading,
    isLoadingFilters,
    allProducts,
    isLoaded,
    meta,
    availableFilters,
    searchQuery,
    selectedSupplier,
    selectedProducer,
    selectedCategory,
    selectedType,
    selectedTax,
    selectedAvailability,
    sorting,
    isLoadingProduct,
    isSavingProduct,
    isDownloading,

    // Getters
    totalPages,
    currentPage,
    filtersCount,
    totalProductsCount,
    filteredProductsCount,

    // Actions
    fetchFilters,
    fetchProducts,
    exportProducts,
    changePage,
    changePerPage,
    setSearchQuery,
    debouncedSetSearchQuery,
    setSupplierFilter,
    setProducerFilter,
    setCategoryFilter,
    setTypeFilter,
    setTaxFilter,
    setAvailabilityFilter,
    setSorting,
    clearAllFilters,
    resetStore,
    clearFilters,
    fetchProduct,
    saveProduct,
    deleteProduct,
    deleteProducts,
  }
})
